import {
    createContext,
    useCallback,
    useContext,
    useRef,
    useState,
} from 'react';
import axios from 'axios';
import axiosInstance from '../authentication/AuthContext';
import { STATUS_LABELS } from '../utils/documentStatus';

const UploadContext = createContext(undefined);

const STATUS_POLL_INTERVAL_MS = 1500;

function sleep(ms, signal) {
    return new Promise((resolve, reject) => {
        const timeout = setTimeout(resolve, ms);
        if (!signal) return;
        if (signal.aborted) {
            clearTimeout(timeout);
            reject(new DOMException('Aborted', 'AbortError'));
            return;
        }
        signal.addEventListener(
            'abort',
            () => {
                clearTimeout(timeout);
                reject(new DOMException('Aborted', 'AbortError'));
            },
            { once: true }
        );
    });
}

export function useUploadContext() {
    const ctx = useContext(UploadContext);
    if (!ctx) {
        throw new Error(
            'useUploadContext must be used within an UploadProvider'
        );
    }
    return ctx;
}

// Mounted once above the router (see src/index.js) so upload progress
// keeps running - and stays visible via the header indicator - even after
// the user navigates away from the Uploader page.
export function UploadProvider({ children }) {
    const [uploads, setUploads] = useState([]);
    const abortControllersRef = useRef({});
    const queueRef = useRef([]);
    const processingRef = useRef(false);

    const updateUpload = useCallback((id, patch) => {
        setUploads((prev) =>
            prev.map((u) => (u.id === id ? { ...u, ...patch } : u))
        );
    }, []);

    const applyDocumentStatus = useCallback(
        (id, document) => {
            const isDone = document.status === 'ingested';
            const isFailed = document.status === 'failed';
            updateUpload(id, {
                status: isDone ? 'success' : isFailed ? 'error' : 'processing',
                phaseLabel: STATUS_LABELS[document.status] || document.status,
                errorMessage: document.error_message || null,
                documentId: document.id,
            });
        },
        [updateUpload]
    );

    const runUpload = useCallback(
        async (item) => {
            const controller = new AbortController();
            abortControllersRef.current[item.id] = controller;
            const { signal } = controller;

            const formData = new FormData();
            formData.append('file', item.file);
            formData.append('filename', item.filename);
            formData.append('uploaded_at', item.uploaded_at);
            if (item.title) formData.append('title', item.title);
            if (item.department)
                formData.append('department', item.department.id);

            updateUpload(item.id, {
                status: 'uploading',
                phaseLabel: 'Starting...',
                errorMessage: null,
            });

            try {
                const response = await axiosInstance.post(
                    '/knowledge/upload/',
                    formData,
                    {
                        headers: {
                            'Content-Type': 'multipart/form-data',
                        },
                        signal,
                        onUploadProgress: (progressEvent) => {
                            const progress = Math.round(
                                (progressEvent.loaded * 100) /
                                    progressEvent.total
                            );
                            updateUpload(item.id, { progress });
                        },
                    }
                );

                let document = response.data;
                applyDocumentStatus(item.id, document);

                // The upload endpoint enqueues a Celery task and returns
                // immediately with status "pending" - keep polling the
                // status endpoint until the task reports a terminal status
                // ("ingested" or "failed").
                while (
                    document.status !== 'ingested' &&
                    document.status !== 'failed'
                ) {
                    await sleep(STATUS_POLL_INTERVAL_MS, signal);

                    let statusResponse;
                    try {
                        statusResponse = await axiosInstance.get(
                            `/documents/${document.id}/status/`,
                            { signal }
                        );
                    } catch (statusErr) {
                        if (statusErr.response?.status === 404) {
                            throw new Error(
                                'Upload failed and was rolled back.'
                            );
                        }
                        throw statusErr;
                    }

                    document = statusResponse.data;
                    applyDocumentStatus(item.id, document);
                }

                if (document.status === 'failed') {
                    throw new Error(
                        document.error_message || 'Document processing failed.'
                    );
                }
            } catch (err) {
                if (axios.isCancel(err) || err.name === 'AbortError') {
                    updateUpload(item.id, {
                        status: 'canceled',
                        phaseLabel: 'Canceled',
                    });
                    return;
                }

                console.error(`Upload failed for ${item.title}:`, err);
                const message =
                    err.response?.data?.error ||
                    err.response?.data?.title?.[0] ||
                    err.message ||
                    'Upload failed.';
                updateUpload(item.id, {
                    status: 'error',
                    phaseLabel: 'Failed',
                    errorMessage: message,
                });
            } finally {
                delete abortControllersRef.current[item.id];
            }
        },
        [applyDocumentStatus, updateUpload]
    );

    const processQueue = useCallback(async () => {
        if (processingRef.current) return;
        processingRef.current = true;
        try {
            // Upload sequentially to avoid overwhelming the server.
            while (queueRef.current.length > 0) {
                const item = queueRef.current.shift();
                await runUpload(item);
            }
        } finally {
            processingRef.current = false;
        }
    }, [runUpload]);

    const enqueueUpload = useCallback(
        (fileMeta) => {
            const item = {
                id: fileMeta.id,
                file: fileMeta.file,
                title: fileMeta.title,
                filename: fileMeta.filename,
                department: fileMeta.department,
                uploaded_at: fileMeta.uploaded_at,
            };

            setUploads((prev) => [
                ...prev,
                {
                    id: item.id,
                    title: item.title || item.filename,
                    filename: item.filename,
                    status: 'queued',
                    phaseLabel: 'Queued...',
                    progress: 0,
                    errorMessage: null,
                    documentId: null,
                },
            ]);

            queueRef.current.push(item);
            processQueue();
        },
        [processQueue]
    );

    const cancelUpload = useCallback((id) => {
        const controller = abortControllersRef.current[id];
        if (controller) controller.abort();
        queueRef.current = queueRef.current.filter((item) => item.id !== id);
        setUploads((prev) => prev.filter((u) => u.id !== id));
    }, []);

    const dismissUpload = useCallback((id) => {
        setUploads((prev) => prev.filter((u) => u.id !== id));
    }, []);

    const activeUploads = uploads.filter(
        (u) =>
            u.status === 'queued' ||
            u.status === 'uploading' ||
            u.status === 'processing'
    );

    const value = {
        uploads,
        activeUploads,
        enqueueUpload,
        cancelUpload,
        dismissUpload,
    };

    return (
        <UploadContext.Provider value={value}>
            {children}
        </UploadContext.Provider>
    );
}
